import { useEffect, useMemo } from 'react';

import usePageDataFetch from 'hooks/usePageDataFetch';
import usePageLanguage from 'hooks/usePageLanguage';
import usePageSlug from 'hooks/usePageSlug';

import withPulse from 'components/Decorators/withPulse';

import AuthWrapper from 'providers/AuthWrapper';
import GlobalProviders from 'providers/GlobalProviders';

import { getDropdownLanguagesList } from 'utils/getFooterDropdownLanguages';
import { isPageLocalized } from 'utils/isPageLocalized';

import JSONLinkedData from 'components/JSONLinkedData';
import { LandingCategory } from 'constants/enums';
import useLandingPropsStore from 'stores/landingPropsStore';

import { SchemaType } from 'components/JSONLinkedData/types';
import { PageType } from 'constants/pages';
import { withApiData } from 'providers/ApiDataProvider';
import useSendPageSlugToDD from 'hooks/useSendPageSlugToDD';
import Landing from './Landing';

function getPageToRender({
  screenName,
  pageData,
  isLocalized,
  languages,
  pageType,
}: {
  screenName: string;
  pageData: any;
  isLocalized: boolean;
  languages: ReturnType<typeof getDropdownLanguagesList>;
  pageType: PageType;
}) {
  switch (pageType) {
    case PageType.Template: {
      // Dynamic template page comes here
      return null;
    }

    case PageType.LandingOrHub:
    default: {
      return (
        <Landing
          screenName={screenName}
          pageData={pageData}
          isLocalized={isLocalized}
          languages={languages}
        />
      );
    }
  }
}

function Page() {
  const { slug: pagePath } = usePageSlug();
  const isHomePage = `/${pagePath}` === '/';
  const { pageData, languageList, storageDataUnits, screenName, pageSlug, renderPage, pageType } =
    usePageDataFetch();
  const { language } = usePageLanguage();
  const isLocalized = useMemo(
    () => isPageLocalized(pageSlug, languageList),
    [languageList, pageSlug],
  );

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useSendPageSlugToDD();

  const dropdownLanguagesList = useMemo(
    () => getDropdownLanguagesList(pageSlug, languageList),
    [pageSlug, languageList],
  );

  const setLandingProps = useLandingPropsStore(x => x.setLandingProps);

  setLandingProps({
    screenName,
    category: pageData?.category || LandingCategory.general,
    premiumPopupPackage: pageData?.premiumPopupPackage,
    promoRequired: pageData?.promoRequired,
    tags: pageData?.Tags,
  });

  return renderPage || pageData ? (
    <GlobalProviders
      isLocalized={isLocalized}
      dropdownLanguagesList={dropdownLanguagesList}
      slug={pageSlug}
      translatedData={pageData?.translations}
      dataFetched={!!pageData}
    >
      <JSONLinkedData
        data={{
          description: isHomePage && pageData?.seoSettings?.title,
        }}
        type={SchemaType.Organization}
      />
      <AuthWrapper
        pageType={pageType}
        storageDataUnits={storageDataUnits}
        monetizationRequired={pageData?.monetizationRequired}
      >
        {getPageToRender({
          screenName,
          pageData,
          isLocalized,
          languages: dropdownLanguagesList,
          pageType,
        })}
      </AuthWrapper>
    </GlobalProviders>
  ) : null;
}

export default withApiData(withPulse(Page));
